<template>
  <component
    :is="as"
    :id="uniqueId"
    :class="[
      $style.button,
      {
        [$style.typeDefault]: type === 'default',
        [$style.typeGhost]: type === 'ghost',
        [$style.typeBare]: type === 'bare',
        [$style.typeToggle]: type === 'toggle',

        [$style.primary]: appearance === 'primary',
        [$style.secondary]: appearance === 'secondary',
        [$style.inverted]: appearance === 'inverted',
        [$style.reverseInverted]: appearance === 'reverseInverted',

        [$style.sizeSmall]: size === 'xsmall',
        [$style.sizeSmall]: size === 'small',
        [$style.sizeMedium]: size === 'medium',
        [$style.sizeLarge]: size === 'large',

        [$style.intentNone]: intent === 'none',
        [$style.intentSuccess]: intent === 'success',
        [$style.intentDestructive]: intent === 'destructive',

        [$style.shadow]: shadow,

        [$style.selected]: selected,
        [$style.hasIconLeft]: !!iconLeft,

        [$style.inert]: isInert,
      },
    ]"
    :style="buttonStyles"
    :data-type="type"
    :type="isNativeButton ? 'button' : undefined"
    :disabled="isNativeButton ? disabled || loading : undefined"
    :aria-disabled="isInert || undefined"
    :tabindex="isInert ? -1 : undefined"
  >
    <SCASvgIcon
      v-if="iconLeft && !loading"
      :icon="iconLeft"
      :size="iconSize"
      :mirror-x="iconMirrorX"
      :mirror-y="iconMirrorY"
    />
    <SCALoader
      v-if="iconLeft && loading"
      size="small"
      :appearance="
        appearance === 'inverted' || (type === 'default' && appearance === 'primary') ? 'white' : 'greyscale'
      "
      indeterminate
    />

    <span v-if="$slots['default']" :class="$style.label"><slot name="default" /></span>
    <slot name="special" />
    <SCASvgIcon v-if="iconRight" :icon="iconRight" :size="iconSize" />
    <SCASvgIcon v-if="hasDropdown" icon="phosphor/caret-down" size="12px" />
    <SCATooltip v-if="tooltip || title" :id="uniqueId" :text="tooltipText ?? ''" />
  </component>
</template>

<script setup lang="ts">
import type { Component } from "vue";
import { computed } from "vue";

import type { IconName } from "./icons/iconNames.generated";
import SCALoader from "./SCALoader.vue";
import SCASvgIcon from "./SCASvgIcon.vue";
import SCATooltip from "./SCATooltip.vue";
import type { ButtonAppearance, ButtonSize, ButtonType, Intent } from "./types";

const props = withDefaults(
  defineProps<{
    type?: ButtonType;
    size?: ButtonSize;
    iconLeft?: IconName;
    iconRight?: IconName;
    iconSize?: string;
    iconMirrorX?: boolean;
    iconMirrorY?: boolean;
    disabled?: boolean;
    hasDropdown?: boolean;
    intent?: Intent;
    loading?: boolean;
    selected?: boolean;
    appearance?: ButtonAppearance;
    shadow?: boolean;
    title?: string;
    tooltip?: string;
    gap?: string;
    paddingLeft?: string;
    paddingRight?: string;
    as?: string | Component;
  }>(),
  {
    type: "default",
    size: "medium",
    iconLeft: undefined,
    iconRight: undefined,
    iconSize: undefined,
    iconMirrorX: false,
    iconMirrorY: false,
    disabled: false,
    hasDropdown: false,
    intent: "none",
    loading: false,
    selected: false,
    appearance: "primary",
    shadow: true,
    title: undefined,
    tooltip: undefined,
    gap: "8px",
    paddingLeft: undefined,
    paddingRight: undefined,
    as: "button",
  },
);

const uniqueId = `btn-${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 8)}`;
const isNativeButton = computed(() => props.as === "button");
const isInert = computed(() => !isNativeButton.value && (props.disabled || props.loading));
const tooltipText = computed(() => props.tooltip ?? props.title);
const buttonStyles = computed(() => ({
  "--button-gap": props.gap,
  "--button-padding-left": props.paddingLeft ?? "12px",
  "--button-padding-right": props.paddingRight ?? "12px",
}));
</script>

<style lang="scss" module>
@use "../styles/generated/figma_typo_mixins.scss" as FigmaTypoMixins;
@use "../styles/icon.scss" as IconMixins;

.button {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: var(--button-gap, var(--space-component-gap-small));
  padding: var(--button-padding-top, 0px)
    var(--button-padding-right, var(--button-padding-horizontal, var(--space-component-padding-medium)))
    var(--button-padding-bottom, 0px)
    var(--button-padding-left, var(--button-padding-horizontal, var(--space-component-padding-medium)));
  flex-shrink: 0;
  @include IconMixins.icon-color(currentColor);

  &:focus {
    outline: none;
  }

  &.sizeSmall {
    @include FigmaTypoMixins.typo-body-medium;
    height: 28px;
    line-height: 28px;
    &.hasIconLeft {
      padding-left: var(--space-component-padding-small);
    }
  }
  &.sizeMedium {
    @include FigmaTypoMixins.typo-body-medium;
    height: var(--size-medium);
  }
  &.sizeLarge {
    @include FigmaTypoMixins.typo-body-large;
    height: var(--size-large);
  }

  // includes loading
  &[disabled] {
    opacity: 0.5;
    svg {
      opacity: 0.5;
    }
  }

  &.inert {
    opacity: 0.5;
    pointer-events: none;
    svg {
      opacity: 0.5;
    }
  }

  &.typeDefault {
    color: var(--color-action-inverted-normal);
    background-color: var(--color-action-primary-normal);

    border-radius: var(--border-radius-large);
    &.shadow {
      box-shadow: 0px 1px 2px 0px rgba(27, 36, 44, 0.12);
    }
    &.primary {
      &.intentNone {
        &:not([disabled]) {
          &:focus-visible,
          &:hover {
            background-color: var(--color-action-primary-hover);
          }
          &:active {
            background-color: var(--color-action-primary-active);
          }
          &.selected {
            background-color: var(--color-action-primary-selected);
          }
        }
      }
      &.intentSuccess {
        background-color: var(--color-action-success-normal);
        &:not([disabled]) {
          &:focus-visible,
          &:hover {
            background-color: var(--color-action-success-hover);
          }
          &:active {
            background-color: var(--color-action-success-active);
          }
          &.selected {
            background-color: var(--color-action-success-selected);
          }
        }
      }
      &.intentDestructive {
        background-color: var(--color-action-destructive-normal);
        &:not([disabled]) {
          &:focus-visible,
          &:hover {
            background-color: var(--color-action-destructive-hover);
          }
          &:active {
            background-color: var(--color-action-destructive-active);
          }
          &.selected {
            background-color: var(--color-action-destructive-selected);
          }
        }
      }
    }
    &.secondary {
      color: var(--color-action-neutral-normal);
      background-color: var(--color-action-inverted-normal);

      border: 1px solid var(--color-action-outline-normal);
      &:not([disabled]) {
        &:focus-visible,
        &:hover {
          border: 1px solid var(--color-action-outline-hover);
        }
        &:active {
          border: 1px solid var(--color-action-outline-active);
        }
        &.selected {
          border: 1px solid var(--color-action-outline-selected);
        }
      }

      &.intentNone {
        &:not([disabled]) {
          &:focus-visible,
          &:hover {
            color: var(--color-action-neutral-hover);
          }
          &:active {
            color: var(--color-action-primary-active);
          }
          &.selected {
            color: var(--color-action-neutral-selected);
          }
        }
      }
      &.intentSuccess {
        color: var(--color-action-success-normal);
        background-color: var(--color-action-inverted-normal);
        &:not([disabled]) {
          &:focus-visible,
          &:hover {
            color: var(--color-action-success-hover);
          }
          &:active {
            color: var(--color-action-success-active);
          }
          &.selected {
            color: var(--color-action-success-selected);
          }
        }
      }
      &.intentDestructive {
        color: var(--color-action-destructive-normal);
        background-color: var(--color-action-inverted-normal);
        &:not([disabled]) {
          &:focus-visible,
          &:hover {
            color: var(--color-action-destructive-hover);
          }
          &:active {
            color: var(--color-action-destructive-active);
          }
          &.selected {
            color: var(--color-action-destructive-selected);
          }
        }
      }
    }
    &.inverted {
      color: var(--color-interaction-default-active);
      background-color: var(--color-content-primary-inverted);

      &.intentNone {
        &:not([disabled]) {
          &:focus-visible,
          &:hover {
            background-color: var(--color-background-informative-muted);
          }
          &:active,
          &.selected {
            color: var(--color-interaction-default-normal);
            background-color: var(--color-background-informative-muted);
          }
        }
      }
    }
  }

  &.typeGhost {
    color: var(--color-action-primary-normal);
    background-color: var(--color-action-ghost-normal);

    border-radius: var(--border-radius-large);
    &.primary {
      &.intentNone {
        &:not([disabled]) {
          &:focus-visible,
          &:hover {
            color: var(--color-action-primary-hover);
            background-color: var(--color-action-ghost-hover);
          }
          &:active {
            color: var(--color-action-primary-active);
            background-color: var(--color-action-ghost-active);
          }
          &.selected {
            color: var(--color-action-primary-selected);
            background-color: var(--color-action-ghost-selected);
          }
        }
      }
      &.intentSuccess {
        color: var(--color-action-success-normal);
        &:not([disabled]) {
          &:focus-visible,
          &:hover {
            color: var(--color-action-success-hover);
            background-color: var(--color-action-ghost-hover);
          }
          &:active {
            color: var(--color-action-success-active);
            background-color: var(--color-action-ghost-active);
          }
          &.selected {
            color: var(--color-action-success-selected);
            background-color: var(--color-action-ghost-selected);
          }
        }
      }
      &.intentDestructive {
        color: var(--color-action-destructive-normal);
        &:not([disabled]) {
          &:focus-visible,
          &:hover {
            color: var(--color-action-destructive-hover);
            background-color: var(--color-action-ghost-destructive-hover);
          }
          &:active {
            color: var(--color-action-destructive-active);
            background-color: var(--color-action-ghost-destructive-active);
          }
          &.selected {
            color: var(--color-action-destructive-selected);
            background-color: var(--color-action-ghost-selected);
          }
        }
      }
    }
    &.secondary {
      color: var(--color-action-neutral-normal);
      &.intentNone {
        &:not([disabled]) {
          &:focus-visible,
          &:hover {
            color: var(--color-action-neutral-hover);
            background-color: var(--color-action-ghost-hover);

            span {
              color: inherit;
            }
          }
          &:active {
            color: var(--color-action-neutral-active);
            background-color: var(--color-action-ghost-active);
          }
          &.selected {
            color: var(--color-action-neutral-selected);
            background-color: var(--color-action-ghost-selected);

            span {
              color: inherit;
            }
          }
        }
      }
    }
    &.inverted {
      color: var(--color-action-inverted-normal);
      background: var(--color-action-ghost-normal);

      &.intentNone {
        &:not([disabled]) {
          &:focus-visible,
          &:hover {
            color: var(--color-action-inverted-hover);
            background-color: var(--color-action-ghost-inverted-hover);
          }
          &:active {
            color: var(--color-action-inverted-active);
            background-color: var(--color-action-ghost-inverted-active);
          }
          &.selected {
            color: var(--color-action-inverted-selected);
            background-color: var(--color-action-ghost-inverted-selected);
          }
        }
      }
    }
    &.reverseInverted {
      color: var(--color-action-reverseInverted-normal);
      &.intentNone {
        &:not([disabled]) {
          &:focus-visible,
          &:hover {
            color: var(--color-action-reverseInverted-hover);
            background-color: var(--color-action-ghost-hover);
          }
          &:active {
            color: var(--color-action-reverseInverted-active);
            background-color: var(--color-action-ghost-active);
          }
        }
      }
    }
  }

  &.typeBare {
    padding: 0px 0px;
    border-radius: var(--border-radius-large);
    &.sizeSmall {
      @include FigmaTypoMixins.typo-body-medium;
      height: var(--size-xSmall);
    }
    &.sizeMedium {
      @include FigmaTypoMixins.typo-body-large;
      height: 28px;
    }

    &.primary {
      &.intentNone {
        color: var(--color-action-primary-normal);
        &:not([disabled]) {
          &:focus-visible,
          &:hover {
            color: var(--color-action-primary-hover);
          }
          &:active {
            color: var(--color-action-primary-active);
          }
          &.selected {
            color: var(--color-action-primary-selected);
          }
        }
      }
      &.intentSuccess {
        color: var(--color-action-success-normal);
        &:not([disabled]) {
          &:focus-visible,
          &:hover {
            color: var(--color-action-success-hover);
          }
          &:active {
            color: var(--color-action-success-active);
          }
          &.selected {
            color: var(--color-action-success-selected);
          }
        }
      }
      &.intentDestructive {
        color: var(--color-action-destructive-normal);
        &:not([disabled]) {
          &:focus-visible,
          &:hover {
            color: var(--color-action-destructive-hover);
          }
          &:active {
            color: var(--color-action-destructive-active);
          }
          &.selected {
            color: var(--color-action-destructive-selected);
          }
        }
      }
    }
    &.secondary {
      color: var(--color-action-neutral-normal);
      &.intent {
        &None {
          &:not([disabled]) {
            &:focus-visible,
            &:hover {
              color: var(--color-action-neutral-hover);
            }
            &:active {
              color: var(--color-action-neutral-active);
            }
            &.selected {
              color: var(--color-action-neutral-selected);
            }
          }
        }
      }
    }
    &.inverted {
      text-decoration: underline;
      color: var(--color-action-inverted-normal);
      &.intentNone {
        &:not([disabled]) {
          &:focus-visible,
          &:hover {
            color: var(--color-action-inverted-hover);
          }
          &:active {
            color: var(--color-action-inverted-active);
          }
          &.selected {
            color: var(--color-action-inverted-selected);
          }
        }
      }
    }
    &.reverseInverted {
      text-decoration: underline;
      color: var(--color-action-reverseInverted-normal);
      &.intentNone {
        &:not([disabled]) {
          &:focus-visible,
          &:hover {
            color: var(--color-action-reverseInverted-hover);
          }
          &:active {
            color: var(--color-action-reverseInverted-active);
          }
          &.selected {
            color: var(--color-action-reverseInverted-selected);
          }
        }
      }
    }
  }

  &.typeToggle {
    color: var(--color-action-inverted-normal);
    background-color: var(--color-semantic-brand-m2);
    border-radius: var(--border-radius-large);
    &.shadow {
      box-shadow: 0px 1px 2px 0px rgba(27, 36, 44, 0.12);
    }

    &.selected:not([disabled]):focus-visible {
      box-shadow: inset 0 0 0 1px var(--color-action-primary-normal);
    }

    &.primary {
      &:not([disabled]) {
        &:focus-visible,
        &:hover {
          background-color: var(--color-semantic-brand-m1);
        }
        &:active {
          background-color: var(--color-action-primary-active);
        }
        &.selected {
          color: var(--color-action-primary-normal);
          background-color: var(--color-action-primary-subtle-selected);
          svg {
            @include IconMixins.icon-color(var(--color-action-primary-normal));
          }
        }
      }
    }

    &.secondary {
      color: var(--color-action-neutral-normal);
      background-color: var(--color-action-inverted-normal);
      border: 1px solid var(--color-action-outline-normal);
      &:not([disabled]) {
        &:focus-visible,
        &:hover {
          color: var(--color-action-neutral-hover);
          border: 1px solid var(--color-action-outline-hover);
        }
        &:active {
          color: var(--color-action-primary-active);
          border: 1px solid var(--color-action-outline-active);
        }
        &.selected {
          background-color: var(--color-action-primary-subtle-selected);
          border: 1px solid var(--color-action-outline-selected);
          svg {
            @include IconMixins.icon-color(var(--color-action-primary-normal));
          }
        }
      }
    }
  }
}
.label {
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  flex-grow: 1;
}
</style>
